import { useState, useEffect, useCallback, useRef } from 'react';

export interface ElementRect {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
}

interface UseElementPositionOptions {
  /** Padding to add around the element */
  padding?: number;
  /** Whether to track position changes */
  enabled?: boolean;
  /** Throttle updates (ms) */
  throttleMs?: number;
}

const defaultRect: ElementRect = {
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  bottom: 0,
  right: 0,
};

/**
 * Hook to track the position of a DOM element
 * Updates on scroll, resize, and DOM changes
 */
export const useElementPosition = (
  selector: string | null,
  options: UseElementPositionOptions = {}
) => {
  const { padding = 0, enabled = true, throttleMs = 16 } = options;

  const [rect, setRect] = useState<ElementRect>(defaultRect);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isElementFound, setIsElementFound] = useState(false);
  const lastUpdateRef = useRef<number>(0);
  const rafRef = useRef<number | null>(null);

  // Find the element by selector
  const findElement = useCallback(() => {
    if (!selector || !enabled) {
      setElement(null);
      setIsElementFound(false);
      return null;
    }

    // Support both CSS selectors and data-tour attributes
    let el: HTMLElement | null = null;

    if (selector.startsWith('[') || selector.startsWith('.') || selector.startsWith('#')) {
      el = document.querySelector<HTMLElement>(selector);
    } else {
      // Treat as data-tour value
      el = document.querySelector<HTMLElement>(`[data-tour="${selector}"]`);
    }

    setElement(el);
    setIsElementFound(!!el);
    return el;
  }, [selector, enabled]);

  // Update the rect with optional throttling
  const updateRect = useCallback(() => {
    const now = Date.now();
    if (now - lastUpdateRef.current < throttleMs) {
      // Schedule update for later
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(updateRect);
      return;
    }

    lastUpdateRef.current = now;

    const el = findElement();
    if (!el) {
      setRect(defaultRect);
      return;
    }

    const domRect = el.getBoundingClientRect();

    setRect({
      top: domRect.top - padding,
      left: domRect.left - padding,
      width: domRect.width + padding * 2,
      height: domRect.height + padding * 2,
      bottom: domRect.bottom + padding,
      right: domRect.right + padding,
    });
  }, [findElement, padding, throttleMs]);

  // Find the scrollable parent of an element
  const findScrollableParent = useCallback((el: HTMLElement): HTMLElement | null => {
    let parent = el.parentElement;
    while (parent) {
      const style = getComputedStyle(parent);
      const overflowY = style.overflowY;
      const overflowX = style.overflowX;

      if (
        (overflowY === 'auto' || overflowY === 'scroll' ||
         overflowX === 'auto' || overflowX === 'scroll') &&
        parent !== document.body &&
        parent !== document.documentElement
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }, []);

  // Check if element is visible within its scrollable container
  const isElementVisibleInContainer = useCallback((el: HTMLElement, container: HTMLElement | null): boolean => {
    const elRect = el.getBoundingClientRect();

    if (container) {
      const containerRect = container.getBoundingClientRect();
      // Check if element is visible within the container
      return (
        elRect.top >= containerRect.top &&
        elRect.bottom <= containerRect.bottom &&
        elRect.left >= containerRect.left &&
        elRect.right <= containerRect.right
      );
    }

    // Check if element is in viewport
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    const verticallyVisible = elRect.top >= 0 && elRect.bottom <= windowHeight;
    const horizontallyVisible = elRect.left >= 0 && elRect.right <= windowWidth;
    return verticallyVisible && horizontallyVisible;
  }, []);

  // Scroll element into view within its container only
  const scrollIntoView = useCallback(() => {
    if (!element) return;

    const scrollableParent = findScrollableParent(element);

    if (scrollableParent) {
      // Scroll within the container only, not the whole page
      if (!isElementVisibleInContainer(element, scrollableParent)) {
        const elRect = element.getBoundingClientRect();
        const containerRect = scrollableParent.getBoundingClientRect();

        // Calculate how much to scroll within the container
        const scrollTop = elRect.top - containerRect.top - (containerRect.height / 2) + (elRect.height / 2);

        scrollableParent.scrollBy({
          top: scrollTop,
          behavior: 'smooth',
        });

        // Update position after scroll animation
        setTimeout(updateRect, 500);
      } else {
        updateRect();
      }
    } else {
      // No scrollable parent found, check if in viewport
      const elRect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const isInViewport = elRect.top >= 0 && elRect.bottom <= windowHeight;

      if (!isInViewport) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        setTimeout(updateRect, 500);
      } else {
        updateRect();
      }
    }
  }, [element, updateRect, findScrollableParent, isElementVisibleInContainer]);

  // Set up observers and event listeners
  useEffect(() => {
    if (!enabled) return;

    // Initial update
    updateRect();

    // Listen for scroll and resize
    const handleUpdate = () => updateRect();

    window.addEventListener('scroll', handleUpdate, { passive: true, capture: true });
    window.addEventListener('resize', handleUpdate, { passive: true });

    // Watch for DOM changes that might affect the element
    const observer = new MutationObserver(() => {
      // Re-find element in case it was added/removed
      updateRect();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden'],
    });

    // Also use ResizeObserver for more accurate size tracking
    let resizeObserver: ResizeObserver | null = null;
    if (element) {
      resizeObserver = new ResizeObserver(handleUpdate);
      resizeObserver.observe(element);
    }

    return () => {
      window.removeEventListener('scroll', handleUpdate, { capture: true });
      window.removeEventListener('resize', handleUpdate);
      observer.disconnect();
      resizeObserver?.disconnect();
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [enabled, element, updateRect]);

  // Re-find element when selector changes
  useEffect(() => {
    updateRect();
  }, [selector, updateRect]);

  return {
    rect,
    element,
    isElementFound,
    scrollIntoView,
    updateRect,
  };
};

export default useElementPosition;
